import { forwardRef } from 'react';
import {
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  styled,
} from '@mui/material';

import {
  buildSelectionAccentTokens,
  SELECTION_ACCENT_DEFAULT,
} from '@/theme/selectionControlTokens';

/**
 * Portal_Library v2.01 "Button / Check box" — 공통 체크박스.
 *
 * 가이드 사양 (Selection Controls 페이지):
 * - 20×20, radius 2px
 * - Selected: bg #06C + 흰 체크
 * - Selected + hover/focus: bg #5C9DDE + 연파랑 halo (4px radius, inset -2)
 * - Enabled(unchecked): 흰 bg + #B7BBC2 border
 * - Enabled + hover/focus: bg #F9FAFB + #06C border + halo
 * - Disabled(checked): bg #D1D8E2
 * - Disabled(unchecked): bg #F9FAFB + #D1D8E2 border
 * - focus = hover (가이드)
 * - Indeterminate(일부 선택): Selected 와 동일 톤(파랑 bg) + 흰 대시
 */

const SIZE = 20;

const CheckSvg = ({ stroke }: { stroke: string }) => (
  <svg width="10" height="7" viewBox="0 0 10 7" fill="none" aria-hidden>
    <path
      d="M1 3.5L3.8 6.3L9 1"
      stroke={stroke}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DashSvg = ({ stroke }: { stroke: string }) => (
  <svg width="10" height="7" viewBox="0 0 10 7" fill="none" aria-hidden>
    <path
      d="M1 3.5H9"
      stroke={stroke}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const IconRoot = styled('span', {
  shouldForwardProp: (p) =>
    p !== 'checked' && p !== 'disabled' && p !== 'accentBase',
})<{ checked: boolean; disabled?: boolean; accentBase: string }>(
  ({ checked, disabled, accentBase }) => ({
    position: 'relative',
    display: 'inline-flex',
    width: SIZE,
    height: SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    // hover halo (부모 :hover/:focus-visible 시에만 색이 입혀짐)
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: -2,
      borderRadius: 4,
      background: 'transparent',
      pointerEvents: 'none',
      transition: 'background 120ms ease',
    },
    // 테마 MuiCheckbox override 의 `& svg { 20×20 }` 확대를 상쇄 — 체크는 10×7 고정
    '& .v2cb-body svg': { width: 10, height: 7 },
    '& .v2cb-body': {
      position: 'relative',
      width: SIZE,
      height: SIZE,
      borderRadius: 2,
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: disabled
        ? checked
          ? 'var(--dk-text-disabled, #D1D8E2)'
          : 'var(--dk-bg-subtle, #F9FAFB)'
        : checked
        ? accentBase
        : 'var(--dk-bg, #FFF)',
      border: disabled
        ? checked
          ? 'none'
          : '1px solid var(--dk-text-disabled, #D1D8E2)'
        : checked
        ? 'none'
        : '1px solid var(--dk-text-dim, #B7BBC2)',
      transition: 'background 120ms ease, border-color 120ms ease',
    },
  }),
);

/**
 * accent 해소. 안 넘기면 모듈 상수를 그대로 써서 **함수 호출조차 하지 않는다** —
 * accent 를 안 쓰는 기존 호출부 16곳에 계산 비용이 붙지 않는다.
 */
const resolveAccent = (accentColor?: string) =>
  accentColor
    ? buildSelectionAccentTokens(accentColor)
    : SELECTION_ACCENT_DEFAULT;

/**
 * 색이 걸리는 셀렉터. 자기 hover/focus 규칙과 `.v2cb-hover-target` 위임 규칙이 원래
 * 한 쌍씩 따로 있었는데, 값이 같으므로 셀렉터만 합쳐 **색 등장 지점을 7곳에서 4곳으로** 줄였다.
 *
 * ⚠️ 아래 네 상수를 스타일 객체에 꽂는 **순서(HALO → ON → OFF → INDETERMINATE_FIX)를
 * 절대 바꾸지 말 것.** 마지막 FIX 는 OFF 와 특이도가 같고 후순위 승리에 의존한다.
 */
const SEL_HALO = [
  '&:not(.Mui-disabled):hover .v2cb-root::before',
  '&:not(.Mui-disabled).Mui-focusVisible .v2cb-root::before',
  '.v2cb-hover-target:hover &:not(.Mui-disabled) .v2cb-root::before',
].join(', ');

/** checked / indeterminate + hover·focus → 바닥이 accent 의 밝은 톤 */
const SEL_ON_BODY = [
  '&:not(.Mui-disabled).Mui-checked:hover .v2cb-root .v2cb-body',
  '&:not(.Mui-disabled).Mui-checked.Mui-focusVisible .v2cb-root .v2cb-body',
  '&:not(.Mui-disabled).MuiCheckbox-indeterminate:hover .v2cb-root .v2cb-body',
  '&:not(.Mui-disabled).MuiCheckbox-indeterminate.Mui-focusVisible .v2cb-root .v2cb-body',
  '.v2cb-hover-target:hover &:not(.Mui-disabled).Mui-checked .v2cb-root .v2cb-body',
  '.v2cb-hover-target:hover &:not(.Mui-disabled).MuiCheckbox-indeterminate .v2cb-root .v2cb-body',
].join(', ');

/** unchecked + hover·focus → 바닥 gray1 + 보더가 accent */
const SEL_OFF_BODY = [
  '&:not(.Mui-disabled):not(.Mui-checked):not(.MuiCheckbox-indeterminate):hover .v2cb-root .v2cb-body',
  '&:not(.Mui-disabled):not(.Mui-checked):not(.MuiCheckbox-indeterminate).Mui-focusVisible .v2cb-root .v2cb-body',
  '.v2cb-hover-target:hover &:not(.Mui-disabled):not(.Mui-checked):not(.MuiCheckbox-indeterminate) .v2cb-root .v2cb-body',
].join(', ');

/**
 * 부분선택(indeterminate)은 checked 가 아니라 위 unchecked hover 규칙에 걸린다.
 * Selected 와 같은 톤을 유지하도록 뒤에서 덮어쓴다(동일 특이도 → 후순위 승).
 */
const SEL_INDETERMINATE_FIX = [
  '&.MuiCheckbox-indeterminate:not(.Mui-disabled):hover .v2cb-root .v2cb-body',
  '&.MuiCheckbox-indeterminate:not(.Mui-disabled).Mui-focusVisible .v2cb-root .v2cb-body',
  '.v2cb-hover-target:hover &.MuiCheckbox-indeterminate:not(.Mui-disabled) .v2cb-root .v2cb-body',
].join(', ');

const StyledCheckbox = styled(MuiCheckbox, {
  // MUI 는 미인식 prop 을 DOM 으로 흘린다. accentColor 는 유효한 HTML 속성이 아니라
  // 그대로 두면 React unknown-prop 경고가 난다
  shouldForwardProp: (p) => p !== 'accentColor',
})<{ accentColor?: string }>(({ accentColor }) => {
  const accent = resolveAccent(accentColor);

  return {
    // hit-area 는 전역 override(theme/overrides/Checkbox.tsx)가 padding 4px 를 걸어
    // 실측 28px(20+8)이다. 좁히려면 사용처에서 sx={{ padding: 0 }} 로 덮는다.
    '&:hover': { backgroundColor: 'transparent' },
    '&.Mui-focusVisible': { outline: 'none' },

    [SEL_HALO]: { background: accent.halo },
    [SEL_ON_BODY]: { background: accent.hover },
    [SEL_OFF_BODY]: {
      background: 'var(--dk-bg-subtle, #F9FAFB)',
      borderColor: accent.base,
    },
    [SEL_INDETERMINATE_FIX]: {
      background: accent.hover,
      borderColor: 'transparent',
    },
  };
});

export type CheckboxProps = MuiCheckboxProps & {
  /**
   * checked 바닥 · hover halo · unchecked hover 보더에 쓰일 accent 색 (base hex 하나).
   * hover 톤과 halo 는 이 값에서 자동 파생된다 (`theme/selectionControlTokens`).
   * 미지정 시 DS 기본 파랑(#0066CC).
   *
   * accent 영향을 **받지 않는** 것 — 가이드 고정값이다:
   * unchecked 기본 보더(#B7BBC2), disabled 전 상태, 흰 체크 마크.
   *
   * ⚠️ 흰 체크 마크와 대비가 나오는 **어두운 색**을 전제한다. 밝은 색을 넘기면 체크가
   * 안 읽힌다 (스토리 `LightAccentCaveat` 참고). 명도 기반 자동 전환은 넣지 않았다 —
   * 가이드에 규칙이 없고, hover 톤까지 적용하면 hover 순간 체크 색이 튄다.
   */
  readonly accentColor?: string;
};

const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ disabled, onKeyDown, accentColor, ...props }, ref) => (
    <StyledCheckbox
      ref={ref}
      disableRipple
      disabled={disabled}
      accentColor={accentColor}
      onKeyDown={(e) => {
        // 체크박스 기본 토글 키는 Space 뿐 — Enter 도 지원.
        // input.click() 이라 사용처 onChange 가 그대로 발화, preventDefault 로 폼 제출 방지
        if (e.key === 'Enter' && !disabled) {
          e.preventDefault();
          e.currentTarget.querySelector('input')?.click();
        }
        onKeyDown?.(e);
      }}
      icon={
        <IconRoot
          checked={false}
          disabled={disabled}
          accentBase={resolveAccent(accentColor).base}
          className="v2cb-root"
        >
          <span className="v2cb-body" />
        </IconRoot>
      }
      checkedIcon={
        <IconRoot
          checked
          disabled={disabled}
          accentBase={resolveAccent(accentColor).base}
          className="v2cb-root"
        >
          <span className="v2cb-body">
            <CheckSvg stroke={disabled ? '#F9FAFB' : '#FFF'} />
          </span>
        </IconRoot>
      }
      indeterminateIcon={
        <IconRoot
          checked
          disabled={disabled}
          accentBase={resolveAccent(accentColor).base}
          className="v2cb-root"
        >
          <span className="v2cb-body">
            <DashSvg stroke={disabled ? '#F9FAFB' : '#FFF'} />
          </span>
        </IconRoot>
      }
      {...props}
    />
  ),
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
